if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

(() => {
  const selector = 'video.product-loop-video';
  const watched = new WeakSet();
  const visible = new WeakMap();
  const userPaused = new WeakSet();
  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  function syncButtons(video) {
    const player = video.closest('.product-loop-player');
    if (!player) return;

    const sound = player.querySelector('[data-video-sound]');
    const play = player.querySelector('[data-video-play]');

    sound.textContent = video.muted ? 'Sound on' : 'Sound off';
    sound.setAttribute(
      'aria-label',
      video.muted ? 'Turn video sound on' : 'Turn video sound off'
    );

    play.textContent = video.paused ? 'Play' : 'Pause';
    play.setAttribute(
      'aria-label',
      video.paused ? 'Play video' : 'Pause video'
    );
  }

  function update(video) {
    if (
      visible.get(video) &&
      !document.hidden &&
      !reducedMotion.matches &&
      !userPaused.has(video)
    ) {
      video.play().catch(() => syncButtons(video));
    } else {
      video.pause();
    }
  }

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      visible.set(
        entry.target,
        entry.isIntersecting && entry.intersectionRatio >= 0.25
      );
      update(entry.target);
    });
  }, { threshold: [0, 0.25] });

  function initialise() {
    document.querySelectorAll(selector).forEach(video => {
      if (watched.has(video)) return;
      watched.add(video);

      video.muted = true;
      video.playsInline = true;
      video.controls = false;

      const player = video.closest('.product-loop-player');
      if (!player) return;

      player.querySelector('[data-video-sound]')
        .addEventListener('click', () => {
          const unmute = video.muted;

          if (unmute) {
            document.querySelectorAll(selector).forEach(other => {
              if (other !== video) other.muted = true;
            });
          }

          video.muted = !unmute;
          syncButtons(video);

          if (unmute) {
            userPaused.delete(video);
            video.play().catch(() => {
              video.muted = true;
              syncButtons(video);
            });
          }
        });

      player.querySelector('[data-video-play]')
        .addEventListener('click', () => {
          if (video.paused) {
            userPaused.delete(video);
            video.play().catch(() => syncButtons(video));
          } else {
            userPaused.add(video);
            video.pause();
          }
        });

      ['play', 'pause', 'volumechange'].forEach(event => {
        video.addEventListener(event, () => syncButtons(video));
      });

      syncButtons(video);
      observer.observe(video);
    });
  }

  function refresh() {
    document.querySelectorAll(selector).forEach(update);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise);
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', initialise);
  document.addEventListener('visibilitychange', refresh);
  reducedMotion.addEventListener('change', refresh);

  document.addEventListener('slideChanged', () => {
    window.setTimeout(refresh, 600);
  });

  document.addEventListener('click', event => {
    if (event.target.closest('media-gallery')) {
      window.setTimeout(refresh, 100);
    }
  });
})();